import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import LanguageSwitcher from '../shared/LanguageSwitcher';
import './Header.css';

const Header = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout, isAdmin, isManager } = useAuth();
  const { cartItemCount } = useCart();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const isHomePage = location.pathname === '/';
  const isScrolled = !isHomePage || scrolled;

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header__container">
        <Link to="/" className="header__logo">
          <span className="header__logo-icon">◆</span>
          <span className="header__logo-text">Marmar Baraka</span>
        </Link>

        <nav className={`header__nav ${mobileMenuOpen ? 'header__nav--open' : ''}`}>
          <Link to="/" className="header__link" onClick={() => setMobileMenuOpen(false)}>
            {t('nav.home')}
          </Link>
          <Link to="/products" className="header__link" onClick={() => setMobileMenuOpen(false)}>
            {t('nav.products')}
          </Link>
          <Link to="/about" className="header__link" onClick={() => setMobileMenuOpen(false)}>
            {t('nav.about')}
          </Link>
          <Link to="/contact" className="header__link" onClick={() => setMobileMenuOpen(false)}>
            {t('nav.contact')}
          </Link>
          {/* Language switcher moved to header actions for a single button */}
        </nav>

        <div className="header__actions">
          <LanguageSwitcher />
          <Link to="/cart" className="header__cart-btn">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 01-8 0" />
            </svg>
            {cartItemCount > 0 && (
              <span className="header__cart-count">{cartItemCount}</span>
            )}
          </Link>

          {isAuthenticated ? (
            <div className="header__user-menu">
              <Link to="/dashboard" className="header__user-btn">
                {user?.firstName}
              </Link>
              {(isAdmin || isManager) && (
                <Link to="/admin" className="header__admin-btn">
                  {t('nav.admin')}
                </Link>
              )}
              <button onClick={handleLogout} className="header__logout-btn">
                {t('nav.logout')}
              </button>
            </div>
          ) : (
            <Link to="/login" className="header__login-btn">
              {t('nav.login')}
            </Link>
          )}

          <button
            className={`header__mobile-toggle ${mobileMenuOpen ? 'header__mobile-toggle--open' : ''}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
